import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  FormControl,
  MenuItem,
  Select,
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import ChannelIcon from "../ChannelIcon";

/**
 * ChannelsFilter (exported as ChannelsFilter from MediaFilter/index.js)
 *
 * Filters the ticket list by communication channel.
 * Follows the same visual pattern as TagsFilter / UsersFilter —
 * renders as an outlined Select in the filter row.
 *
 * Options:
 *   ""           → הכל (no filter — show all channels)
 *   "whatsapp"   → WhatsApp
 *   "facebook"   → Messenger
 *   "instagram"  → Instagram
 *   "email"      → Email
 *   "telegram"   → Telegram
 *
 * Props:
 *   onFiltered: (channel: string|null) => void
 */

const CHANNEL_OPTIONS = [
  { value: "whatsapp",  label: "WhatsApp",  color: "#25d366" },
  { value: "facebook",  label: "Messenger", color: "#0062E0" },
  { value: "instagram", label: "Instagram", color: "#d6249f" },
  { value: "email",     label: "Email",     color: "#6366f1" },
  { value: "telegram",  label: "Telegram",  color: "#2aabee" },
];

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    selectButton: {
      backgroundColor: isDark ? "#131614" : "#fff",
      borderRadius: 10,
      transition: "border-color 0.18s ease, background-color 0.18s ease",
      "&:hover": {
        backgroundColor: isDark
          ? "rgba(46, 227, 122, 0.06)"
          : "rgba(37, 211, 102, 0.06)",
      },
      "&.active": {
        backgroundColor: isDark
          ? "rgba(37,211,102,0.10)"
          : "rgba(37,211,102,0.06)",
      },
      "& .MuiSelect-outlined": {
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 14,
        paddingRight: 32,
        fontSize: 13,
        fontWeight: 600,
        color: isDark ? "#e4e4e7" : "#0f172a",
      },
      "& .MuiSelect-icon": {
        color: isDark ? "#a8acb3" : "#64748b",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderWidth: 1,
        borderColor: isDark
          ? "rgba(255, 255, 255, 0.07)"
          : "#eaeaea",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark
          ? "rgba(46, 227, 122, 0.4)"
          : "#86efac",
      },
      "&.active .MuiOutlinedInput-notchedOutline": {
        borderColor: "#25d366",
      },
    },
    menuItem: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
    },
  };
});

/**
 * ChannelsFilter — controlled or uncontrolled.
 *
 * Controlled:   pass `value` (string | null) and `onFiltered`.
 *               Parent owns the state; component just renders.
 * Uncontrolled: omit `value` — component manages its own state.
 *
 * Controlled mode is used by TicketsManagerTabs so the parent can:
 *   1. Reset the filter when the user switches tabs.
 *   2. Persist the selected channel in the URL (?channel=).
 *   3. Restore the filter from the URL on page load.
 */
export function ChannelsFilter({ onFiltered, value: valueProp }) {
  const classes = useStyles();
  // Internal state only used when the component is uncontrolled (no value prop).
  const [internal, setInternal] = useState(valueProp ?? "");

  // If a value prop is provided, honour it (controlled); otherwise use internal.
  const isControlled = valueProp !== undefined;
  const selected = isControlled ? (valueProp || "") : internal;

  const handleChange = (event) => {
    const raw = event.target.value;
    const val = raw === "" ? null : raw;
    if (!isControlled) setInternal(raw);
    onFiltered(val);
  };

  const isActive = selected !== "";

  return (
    <Box style={{ paddingRight: 5 }}>
      <FormControl fullWidth margin="dense">
        <Select
          className={`${classes.selectButton}${isActive ? " active" : ""}`}
          displayEmpty
          variant="outlined"
          value={selected}
          onChange={handleChange}
          MenuProps={{
            anchorOrigin:    { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top",    horizontal: "left" },
            getContentAnchorEl: null,
          }}
          renderValue={(val) => {
            if (!val) return <em>{i18n.t("channelFilter.label")}</em>;
            const opt = CHANNEL_OPTIONS.find(o => o.value === val);
            return opt ? (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <ChannelIcon channel={val} size={14} />
                {opt.label}
              </span>
            ) : <em>{i18n.t("channelFilter.label")}</em>;
          }}
        >
          {/* "הכל" — clears channel filter */}
          <MenuItem value="" className={classes.menuItem}>
            <span>{i18n.t("channelFilter.all")}</span>
          </MenuItem>

          {CHANNEL_OPTIONS.map(({ value, label }) => (
            <MenuItem key={value} value={value} className={classes.menuItem}>
              <ChannelIcon channel={value} size={16} />
              <span>{label}</span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

// Legacy alias — keeps any existing `import { MediaFilter }` working
export { ChannelsFilter as MediaFilter };
